import { useEffect, useContext } from "react";
import { useSchema } from "../contexts/schema.context";
import { SchemaRegistry } from "../../services/schema/registry/schema.registry";
import { AppCtx } from "../contexts/app.context";
import { toAbsoluteUrl, getBaseUrl } from "../utils/getBaseUrl";
import { DeprecatedAlgoliaSearchProduct } from "../types/Algolia";
import { Hit } from "instantsearch.js";
import { BaseHit } from "instantsearch.js/es/types";
import { transformAlgoliaProductToProductData } from "../../services/schema/utils";

interface OfferCatalogSchemaProps {
  products: Hit<DeprecatedAlgoliaSearchProduct & BaseHit>[];
  title?: string;
  description?: string;
  slug: string;
  gender: string;
  breadcrumbs?: { name: string; url?: string }[];
}

/**
 * Custom hook for managing offer catalog schema generation
 * @param props - Offer catalog data including products, title, description, slug, and breadcrumbs
 */
export const useOfferCatalogSchema = ({
  products,
  title,
  description,
  slug,
  gender,
  breadcrumbs,
}: OfferCatalogSchemaProps): void => {
  const { addSchemas, removeSchemas } = useSchema();
  const { locale } = useContext(AppCtx);

  useEffect(() => {
    const schemaId = `offer-catalog-${gender}-${slug}`;

    if (!products || products.length === 0) {
      removeSchemas(schemaId);
      return;
    }

    const catalogUrl = toAbsoluteUrl(`/${gender}/${slug}`) || "";
    const catalogName = title || `${gender} ${slug}`;
    const catalogDescription = description || `Shop ${catalogName} at Otrium`;

    // Transform Algolia products to ProductData format using the reusable transformer
    const transformedProducts = products.map((hit) => {
      const product = hit;

      return transformAlgoliaProductToProductData(product, {
        baseUrl: getBaseUrl(locale),
        defaultCurrency: "EUR",
        sellerName: "Otrium",
        gender,
        locale,
      });
    });

    // Generate schemas using the registry
    const schemas = SchemaRegistry.offerCatalog({
      offerCatalog: {
        name: catalogName,
        description: catalogDescription,
        url: catalogUrl,
        inLanguage: locale,
        products: transformedProducts,
        breadcrumbs,
      },
    });

    addSchemas(schemas, schemaId);

    // Cleanup on unmount
    return () => removeSchemas(schemaId);
  }, [
    products,
    title,
    description,
    slug,
    gender,
    breadcrumbs,
    locale,
    addSchemas,
    removeSchemas,
  ]);
};
